import { Component, OnInit, inject, input, output, signal } from '@angular/core';
import {
  IHtstSensorDetails,
  MacAddress,
  MatrixCode,
  SerialNumber,
} from '../../models/classes/sensor';
import { I18nService } from '../../services/i18n.service';
import { UiService } from '../../services/ui.service';
import { AssetsService } from '../../services/asset.service';
import { InstructionPopupComponent } from '../instruction-popup/instruction-popup';
import { HtstColorCodeComponent } from '../htst-color-code/htst-color-code';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { InstructionPopupItem } from '../../models/popup.model';
import { SettingsService } from '../../services/settings.service';
import { Marker } from '../../../lib/models/classes/marker';

@Component({
  selector: 'app-htst-outlet-sensor',
  imports: [HtstColorCodeComponent, NzIconModule, InstructionPopupComponent],
  templateUrl: './htst-outlet-sensor.html',
  styleUrls: ['./htst-outlet-sensor.scss'],
})
export class HtstOutletSensorComponent implements OnInit {
  hasCamera = input<boolean>(false);
  settingService = inject(SettingsService);
  allowedInletDigits = input<any[]>([]);
  qrDigits = input<SerialNumber>(new SerialNumber('i'));
  qrDAU = input.required<MacAddress>();
  qrOutletGateway = input.required<MatrixCode>();
  assetID = input.required<any>();
  wizardsteps = input<string[]>([]);
  currentstep = input<number>(0);
  counter = input<number>(0);
  inletSerialNumberEvent = output<SerialNumber>();
  openScannerEvent = output<number>();
  outletSensorHtst = output<IHtstSensorDetails>();
  navBack = output<void>();
  navExit = output<void>();
  navContinue = output<void>();
  navHeader = output<number>();

  showGatewayErrorPopup = signal(false);
  showInletQrLocationPopup = signal(false);
  isFaultyQrCode = signal(false);
  colorData = signal<any>(null);
  selectedColor = signal<any>(null);
  gatewayErrInstruction = signal<InstructionPopupItem[]>([]);
  qrLocationInlet = signal<any[]>([]);

  mappedCodeUpdate = signal<any>('');
  private allowedDigits: Record<string, any[]> = { i: [] };
  inletModelNumbersOption = ['A-SE-23-0002'];
  inletModelNumberSelected = this.inletModelNumbersOption[0];
  ModelID = 'A-DU-5101L-0000';
  dauValue = this.settingService.commonDauInput;

  i18n = inject(I18nService);
  ui = inject(UiService);
  assetsService = inject(AssetsService);
  isMobile = this.ui.IsMobile;
  isTablet = this.ui.IsTablet;

  ngOnInit(): void {
    this.fetchSensorColorData();
    this.allowedDigits['i'] = this.allowedInletDigits();

    const currentQr = this.qrDigits();
    this.checkAllowedModelNumber(currentQr.modelNumber);

    if (!currentQr.modelNumber?.length) {
      currentQr.modelNumber = this.inletModelNumbersOption[0];
    }

    this.checkDeviceDigits(currentQr);
  }

  fetchSensorColorData() {
    this.ModelID = 'A-DU-5101L-0000';
    this.assetsService
      .getSensorColorData(
        this.dauValue.replace(/:/g, '') || this.settingService.inletDauValue.value,
        this.ModelID,
        this.assetID(),
        this.settingService.inletGateWayValue.value
      )
      .then((data: any) => {
        const errorCode = data['ErrorCode']?.toLowerCase();
        if (errorCode === 'noavailablesensor' || errorCode === 'gatewayerror') {
          this.showGatewayErrorPopup.set(true);
          this.gatewayErrInstruction.set([
            { image: 'assets/images/connection_error.png' },
            {
              title:
                errorCode === 'gatewayerror'
                  ? this.i18n.langString('dau_with_exist_gateway') + data['Existing_GatewayID']
                  : this.i18n.langString('connection_capacity_popup_title'),
            },
          ]);
        } else {
          this.showGatewayErrorPopup.set(false);
        }
        this.colorData.set(data['Sensors']);
      });
  }

  checkDeviceDigits(code: SerialNumber) {
    code.mappingValid = false;
    this.checkAllowedModelNumber(code.modelNumber);
    if (!code.modelNumberValid) {
      code.modelNumber = this.inletModelNumberSelected;
      this.isFaultyQrCode.set(true);
    }
    if (code.validLength) {
      this.mappedCodeUpdate.set(code);

      if (
        this.allowedDigits[this.mappedCodeUpdate().device].some(
          (d) => d == this.mappedCodeUpdate().value
        )
      ) {
        if (this.mappedCodeUpdate().modelNumberValid) this.mappedCodeUpdate().mappingValid = true;
      } else {
        this.assetsService
          .checkDeviceMapping(this.mappedCodeUpdate().value, this.mappedCodeUpdate().device)
          .then((res: any) => {
            if (this.mappedCodeUpdate().modelNumberValid)
              this.mappedCodeUpdate().mappingValid = res.isValid;
          });
      }
      this.inletSerialNumberEvent.emit(code);
    }
  }

  checkAllowedModelNumber(modelNumber: string) {
    this.qrDigits().modelNumberValid = this.inletModelNumbersOption.includes(modelNumber);
  }

  getColor(color: any) {
    this.selectedColor.set(color);
    this.outletSensorHtst.emit(color);
  }

  toggleQrLocationInletPopup(show: boolean) {
    this.showInletQrLocationPopup.set(show);

    // re-initialize collection so i18n can serve the current language
    return this.isMobile()
      ? this.qrLocationInlet.set([
          { title: this.i18n.langString('where_is_qr_code') },
          {
            text: this.i18n.langString('inlet_sensor_qr_instruction'),
          },
          {
            text: this.i18n.langString('inlet_sensor_qr_instruction_step1'),
          },
          {
            text: this.i18n.langString('inlet_sensor_qr_instruction_step2'),
            textbold: true,
          },
          {
            text: this.i18n.langString('inlet_sensor_qr_instruction_step3'),
            image: 'assets/images/inlet_sensor_qr_instruction_1.png',
            marker: new Marker(0, '', 0.61, 0.35, 0.5),
          },
          {
            image: 'assets/images/inlet_sensor_qr_instruction_2.png',
            marker: new Marker(0, '', 0.45, 0.35, 0.5),
          },
          {
            image: 'assets/images/inlet_sensor_qr_instruction_3.png',
            marker: new Marker(0, '', 0.49, 0.35, 0.5),
          },
        ])
      : this.qrLocationInlet.set([
          { title: this.i18n.langString('where_is_qr_code') },
          {
            text: this.i18n.langString('inlet_sensor_qr_instruction'),
          },
          {
            text: this.i18n.langString('inlet_sensor_qr_instruction_step1'),
          },
          {
            text: this.i18n.langString('inlet_sensor_qr_instruction_step2'),
            textbold: true,
          },
          {
            text: this.i18n.langString('inlet_sensor_qr_instruction_step3'),
            image: 'assets/images/inlet_sensor_qr_instruction_1.png',
            marker: new Marker(0, '', 0.55, 0.2, 0.5),
          },
          {
            image: 'assets/images/inlet_sensor_qr_instruction_2.png',
            marker: new Marker(0, '', 0.38, 0.2, 0.5),
          },
          {
            image: 'assets/images/inlet_sensor_qr_instruction_3.png',
            marker: new Marker(0, '', 0.4, 0.2, 0.5),
          },
        ]);
  }

  toggleGatewayErrorPopup(show: boolean) {
    this.showGatewayErrorPopup.set(show);
  }
}
